/**
 * 数据占用(锁)相关共享工具 —— 修复 3 + 数据卡一键解锁共用(老板 2026-08-28)。
 *
 * 接口能力盘点(仅前端视角,未改后端):
 * - 后端锁冲突错误 DATASET_DATA_EXIST_ANNOTATE 的 message 由 buildLockHolderMessage 生成,
 *   形如「数据已被 张三、李四 占用,解锁请联系管理员」——含姓名、不含开始时间;
 * - GET /data/findLockRecordByDatasetId:无权限门槛,返回 [{recordId, lockedBy(昵称), lockedNum}],
 *   同样不含时间;
 * - GET /data/admin/locks:需 user:manage,返回原始 DataAnnotationRecord
 *   [{id, datasetId, itemType, createdBy(userId), createdAt}]——含时间、不含姓名。
 *   姓名需前端用 findLockRecordByDatasetId 按 recordId===id 关联补齐。
 *
 * 结论:标注员视角只能拿到姓名(无时长);管理员视角可拿到姓名+时长。
 */
import { h } from 'vue';
import { Modal } from 'ant-design-vue';
import LockErrorContent from '/@/components/LockErrorContent.vue';
import { getLockedRecordByDataset, adminForceUnLock, unLockApi } from '/@/api/business/dataset';

/** 锁占用错误码(与 enums/httpEnum.ts ResultEnum.DATASET_DATA_EXIST_ANNOTATE 同值) */
export const LOCK_OCCUPIED_CODE = 'DATASET_DATA_EXIST_ANNOTATE';

/** 判断异常是否为"数据被他人占用"错误(axios 拦截器对此码 reject 的是原始响应体) */
export function isLockOccupiedError(e: any): boolean {
  return e?.code === LOCK_OCCUPIED_CODE;
}

/** 从后端 message「数据已被 张三、李四 占用,…」解析占用人姓名串;失败返回 null */
export function parseLockHolderNames(message?: string | null): string | null {
  if (typeof message !== 'string' || !message) return null;
  const m = message.match(/数据已被\s*(.+?)\s*占用/);
  const names = (m?.[1] || '').trim();
  return names || null;
}

/** 锁开始时间 → 「x 分钟 / x 小时 y 分钟 / x 天 y 小时」相对时长;解析失败返回 '' */
export function formatLockDuration(iso?: string | null): string {
  if (!iso) return '';
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return '';
  const diff = Date.now() - t;
  if (diff < 60_000) return '刚刚';
  const min = Math.floor(diff / 60_000);
  if (min < 60) return `${min} 分钟`;
  const hour = Math.floor(min / 60);
  if (hour < 24) return `${hour} 小时 ${min % 60} 分钟`;
  const day = Math.floor(hour / 24);
  return `${day} 天 ${hour % 24} 小时`;
}

/** itemType 枚举 → 中文(与后端 B-3 提示口径一致) */
export function lockItemTypeText(itemType?: string | null): string {
  if (itemType === 'SCENE') return '整体/连续帧';
  if (itemType === 'SINGLE_DATA') return '单条数据';
  return itemType || '—';
}

/** ISO 时间 → 本地可读时间(YYYY-MM-DD HH:mm:ss) */
export function formatLockTime(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso).replace('T', ' ').slice(0, 19);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(
    d.getMinutes(),
  )}:${p(d.getSeconds())}`;
}

/**
 * 按"占用人姓名 + 数据集"强制解锁(数据卡悬停红锁 / 弹窗内行级解锁共用)。
 * data_annotation_record 以 (dataset_id, created_by) 唯一,同一人在同一数据集只有一条
 * 锁记录,findLockRecordByDatasetId 的 lockedBy 即昵称,据此定位 recordId。
 * 优先走管理员强制解锁(user:manage);无该权限的管理角色(task:assign/data:audit)
 * 降级走通用 unLockByLockRecordIds(后端未加权限门槛,与既有 UnLock.vue 同源)。
 */
export async function forceUnlockByHolder(
  datasetId: number | string,
  holderName: string,
): Promise<void> {
  const rows: any[] = (await getLockedRecordByDataset({ datasetId })) || [];
  const row =
    rows.find((r) => r?.lockedBy === holderName) ||
    rows.find((r) => String(r?.lockedBy || '').includes(holderName));
  if (!row?.recordId) {
    throw new Error('未找到该占用者的占用记录,可能已被释放,请刷新列表后重试');
  }
  try {
    await adminForceUnLock(row.recordId);
  } catch (e) {
    // 非 user:manage 的管理角色:admin/forceUnLock 会被 403,降级为通用批量解锁
    await unLockApi({ lockRecordIds: [row.recordId] });
  }
}

/** 强制解锁统一确认弹窗(与占用管理/被锁提示同一文案口径,老板 2026-08-28) */
export function confirmForceUnlock(
  onOk: () => Promise<any>,
  extra?: {
    dataName?: string;
    holderName?: string;
    lockedNum?: number | null;
    onCancel?: () => void;
  },
) {
  const count = extra?.lockedNum;
  const scope = extra?.holderName
    ? `将释放「${extra.holderName}」在当前数据集的所选占用记录${
        typeof count === 'number' && Number.isInteger(count) && count >= 0
          ? `（当前占用 ${count} 条）`
          : ''
      }。`
    : `${extra?.dataName ? `与「${extra.dataName}」关联的` : '所选'}占用记录将被释放。`;
  return Modal.confirm({
    title: '确认强制解锁占用？',
    content: `${scope}该记录可能关联多条数据，并非只解锁当前点击的一条。已保存的标注内容保留，原占用者未保存的修改可能无法提交。`,
    okText: '强制解锁',
    okButtonProps: { danger: true },
    cancelText: '取消',
    onOk: () => onOk(),
    onCancel: () => extra?.onCancel?.(),
  });
}

/**
 * 被锁提示弹窗(修复 3):标注员看到「该数据正被 **姓名** 标注,请稍后或联系管理员解锁」;
 * 管理员(user:manage)追加本数据集占用表(姓名/开始时间/已持续)+ 行级「强制解锁」。
 */
export function showDataLockOccupiedModal(opts: {
  message?: string | null;
  datasetId?: number | string | null;
}) {
  Modal.warning({
    title: '数据被占用',
    width: 640,
    style: { maxWidth: 'calc(100vw - 32px)' },
    okText: '关闭',
    content: h(LockErrorContent as any, {
      holderText: parseLockHolderNames(opts.message) || '',
      backendMessage: opts.message || '',
      datasetId: opts.datasetId == null ? null : Number(opts.datasetId) || (opts.datasetId as any),
    }),
  });
}
